import { useState } from 'react';
import { IconCalendarPlus, IconDownload } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { useToast } from '@hana/ui';
import { CERTS, SUGGESTIONS, type Course } from '../data/courses';
import { ctaOf, dueSoon, dueTone, learning, mySoonCerts, openRequired, overdueCount, pctLabel, progress, ring } from '../data/logic';
import s from './compact.module.css';

// Trang chủ Edu khổ iPad (Mint-Training-iPad) và điện thoại (Mint-Training-Phone), app `home`.
// iPad: 3 ô KPI · Đang học lưới 2 cột · 3 cột Bắt buộc / Gợi ý / Chứng chỉ. Điện thoại: một cột, Đang học cuộn ngang.

const GOAL_TICKS = 12;
const ON_TIME = 0.86;
const ON_TIME_GOAL = 0.9;
const barColor = (c: Course) => (c.passed ? 'var(--ok-dot)' : c.late ? 'var(--d-amber)' : 'var(--d-blue)');

function Ring({ p, color, size, text, fontSize, label }: { p: number; color: string; size: number; text: string; fontSize: number; label?: string }) {
  return (
    <svg {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })} width={size} height={size} viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="18" fill="none" stroke="var(--line)" strokeWidth="6" />
      <circle cx="24" cy="24" r="18" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={ring(p)} transform="rotate(-90 24 24)" />
      <text x="24" y={fontSize > 12 ? 28 : 27.5} textAnchor="middle" fontSize={fontSize} fontWeight="600" fill="var(--tx)" fontFamily="Inter, sans-serif">{text}</text>
    </svg>
  );
}

export function HomeCompact({ viewer, seat, open, phone }: { viewer: string; seat: string; open: (c: Course) => void; phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const [on, setOn] = useState<Record<number, boolean>>({});
  const mine = learning();
  const due = dueSoon();
  const sugs = SUGGESTIONS[viewer as 'DAVID'] ?? SUGGESTIONS.DAVID;
  const soon = mySoonCerts(viewer, CERTS);
  const goalAt = Math.round(ON_TIME_GOAL * GOAL_TICKS);
  const onAt = Math.round(ON_TIME * GOAL_TICKS);
  const kpis = [
    { k: phone ? 'Bắt buộc' : 'Bắt buộc đang mở', v: String(openRequired()), tone: '', tip: 'Khoá bắt buộc theo ghế chưa xong' },
    { k: 'Quá hạn', v: String(overdueCount()), tone: 'err', tip: 'Quá hạn task TRN' },
    { k: phone ? 'Đúng hạn' : 'Đúng hạn quý 4', v: `${Math.round(ON_TIME * 100)}%`, tone: '', tip: phone ? 'Quý 4 · mục tiêu 90%' : 'Mục tiêu 90%', bar: true },
  ];
  const renew = () => toast({ msg: t('ed.renewed') });

  const learnCard = (c: Course) => {
    const cta = ctaOf(c);
    const tone = c.done === c.total ? 'go' : c.late ? 'err' : undefined;
    return (
      <button key={c.id} type="button" className={s.lc} aria-label={`${c.k} · ${cta}`} onClick={() => open(c)}>
        <Ring p={progress(c)} color={barColor(c)} size={phone ? 64 : 58} text={pctLabel(c)} fontSize={9.5} />
        <div className={s.lcBody}>
          <b>{c.short}</b>
          <span className={s.pill} data-tone={tone}>{cta}</span>
        </div>
      </button>
    );
  };

  const certs = soon.map((c) => {
    const col = c.status === 'exp' ? 'var(--err)' : 'var(--warn)';
    const aria = `Còn ${c.days} ngày tới hạn ${c.expires}`;
    return (
      <div key={c.k} className={s.certBlock}>
        <div className={s.certRow}>
          <Ring p={Math.min(1, (c.days ?? 0) / 30)} color={col} size={phone ? 56 : 60} text={String(c.days)} fontSize={13} label={aria} />
          <div className={s.certBody}><b>{c.k}</b><span style={{ color: col }}>{c.expires}</span></div>
          {phone && <button type="button" className={s.renewPh} onClick={renew}><IconCalendarPlus size={16} aria-hidden="true" />{t('ed.extend')}</button>}
        </div>
        {!phone && <button type="button" className={s.renew} onClick={renew}><IconDownload size={16} aria-hidden="true" />{t('ed.renew')}</button>}
      </div>
    );
  });

  const dueSec = (
    <section className={s.card} aria-label="Bắt buộc sắp hạn">
      <b className={s.h}>{t('ed.dueSoon')}</b>
      {due.map((c) => (
        <button key={c.id} type="button" className={s.row} onClick={() => open(c)}>
          <span className={s.dueBox} data-tone={dueTone(c.dd!)} title={`Hạn ${c.due} · ${c.dueSub}`}>
            <b>{c.dd}</b><span>{c.dd! < 0 ? 'trễ' : 'ngày'}</span>
          </span>
          <b className={s.name}>{c.short}</b>
          {phone && <span className={s.pct}>{pctLabel(c)}</span>}
        </button>
      ))}
    </section>
  );

  const sugSec = (
    <section className={s.card} aria-label="Gợi ý theo ghế">
      <div className={s.hRow}><b className={s.h}>{t('ed.forSeat')}</b><span className={s.seat}>{seat}</span></div>
      {sugs.map((g, i) => {
        const picked = !!on[i];
        const label = `${picked ? 'Đã thêm vào' : 'Thêm vào'} Khoá học: ${g.k}`;
        return (
          <div key={g.k} className={s.row}>
            <b className={s.name}>{g.k}</b>
            <span className={s.min}>{g.minutes}'</span>
            <button
              type="button"
              className={s.add}
              aria-pressed={picked}
              aria-label={label}
              title={label}
              onClick={() => {
                setOn({ ...on, [i]: !picked });
                toast({ msg: `${picked ? 'Đã bỏ khỏi' : 'Đã thêm vào'} Khoá học: ${g.k}` });
              }}
            >{picked ? '✓' : '+'}</button>
          </div>
        );
      })}
    </section>
  );

  const certSec = phone
    ? soon.length > 0 ? soon.map((c, i) => <section key={c.k} className={`${s.card} ${s.certPh}`} aria-label="Chứng chỉ sắp hết hạn">{certs[i]}</section>) : null
    : (
      <section className={s.card} aria-label="Chứng chỉ sắp hết hạn">
        <b className={s.h}>{t('ed.certsSoon')}</b>
        {certs}
        {!soon.length && <div className={s.allOk}>✓ {t('ed.allValid')}</div>}
      </section>
    );

  return (
    <div className={s.page} data-phone={phone ? '1' : undefined}>
      <div className={s.kpis}>
        {kpis.map((k) => (
          <div key={k.k} className={`${s.card} ${s.kpi}`} title={k.tip}>
            <div className={s.kpiK}>{k.k}</div>
            <b data-tone={k.tone}>{k.v}</b>
            {k.bar && (
              <div className={s.ticks} role="img" aria-label={`Hoàn thành đúng hạn ${Math.round(ON_TIME * 100)}%, mục tiêu ${Math.round(ON_TIME_GOAL * 100)}%`}>
                {Array.from({ length: GOAL_TICKS }, (_, i) => <i key={i} data-on={i < onAt ? '1' : undefined} data-goal={i === goalAt - 1 && i >= onAt ? '1' : undefined} />)}
              </div>
            )}
          </div>
        ))}
      </div>
      <b className={s.sec}>{t('ed.learning')}</b>
      <div className={s.learn}>{mine.map(learnCard)}</div>
      {phone ? <>{dueSec}{sugSec}{certSec}</> : <div className={s.cols}>{dueSec}{sugSec}{certSec}</div>}
    </div>
  );
}
